import * as PIXI from "pixi.js";
import { ColorMatrixFilter, Container, Filter, Sprite, Texture } from "pixi.js";
import { ITransform } from "./ITransform";
import { IBehaviour } from "./behaviours/IBehaviour";
import { Game } from "./Game";
import { DebugShade } from "./DebugShade";
import { Geom } from "./Geom";
import { DamageTick } from "./DamageTick";
import { Colors } from "./Colors";

export class Enemy implements ITransform {
	public container: PIXI.Container = new Container();
	private readonly sprite: Sprite;
	public readonly hitbox: DebugShade = new DebugShade()
	public readonly behaviour: IBehaviour;
	public speed: number = 1;
	public destroyed: boolean = false;
	public size: number = 30;
	public health: number = 1;
	private takingDamageAnimationProgress: number = -1;
	public aggroStartRange: number = 250;
	public aggroStopRange: number = 600;
	public aggro: boolean = true;
	public preferredSpacing: number = 0;
	public minSpacing: number = 0;
	public dead: boolean = false;
	isGrounded: boolean = true;
	damage: number = 1;

	public constructor(parent: Container, texture: Texture, behaviour: IBehaviour) {
		this.sprite = new PIXI.Sprite(texture);
		this.sprite.anchor.x = 0.5;
		this.sprite.anchor.y = 0.5;
		this.sprite.roundPixels = true;
		this.hitbox.size = 30;
		this.behaviour = behaviour;

		parent.addChild(this.container);
		this.container.addChild(this.sprite);
		this.container.addChild(this.hitbox.graphics);
	}

	public destroy() {
		this.container.removeFromParent()
		this.destroyed = true;
	}

	public tick(deltaMS: number, game: Game) {
		this.hitbox.graphics.visible = game.showHitboxes;
		if (game.showHitboxes) {
			if (!this.aggro) this.hitbox.size = this.aggroStartRange
			else this.hitbox.size = this.size;
		}

		if (this.dead) {
			this.updateDeathAnimation(deltaMS);
			return;
		}

		if (this.aggro) {
			for (const enemy of game.enemies) {
				if (enemy == this) continue;
				if (enemy.dead) continue;
				let distance = Geom.distance(this, enemy);
				if (distance < this.minSpacing || distance < this.size + enemy.size) {
					Geom.moveTowards(this, enemy, -deltaMS * 0.05);
				} else if (distance < this.preferredSpacing) {
					Geom.moveTowards(this, enemy, -deltaMS * 0.01);
				}
			}
			this.sprite.alpha = 1;
		} else {
			this.sprite.alpha = 0.6;
		}

		const distanceToPlayer = Geom.distance(this, game.player);
		if (this.aggro && distanceToPlayer > this.aggroStopRange) {
			this.aggro = false;
			this.hitbox.alpha = 0.1;
			this.hitbox.size = this.aggroStartRange;
		}
		if (!this.aggro && distanceToPlayer < this.aggroStartRange) {
			this.aggro = true;
			this.hitbox.alpha = 0.2;
			this.hitbox.size = this.size;
		}
		if (this.aggro) this.behaviour.tick(deltaMS, this, game);
		if (this.isGrounded) {
			if (this.container.x - this.size < game.ground.minX) {
				this.container.x = game.ground.minX + this.size;
			}
			if (this.container.x + this.size > game.ground.maxX) {
				this.container.x = game.ground.maxX - this.size;
			}
			if (this.container.y - this.size < game.ground.minY) {
				this.container.y = game.ground.minY + this.size;
			}
			if (this.container.y + this.size > game.ground.maxY) {
				this.container.y = game.ground.maxY - this.size;
			}
		}

		if (this.health <= 0) this.dead = true;

		if (this.takingDamageAnimationProgress >= 0) {
			this.takingDamageAnimationProgress += deltaMS;
			if (this.takingDamageAnimationProgress < 400) {
				(this.sprite.filters as ColorMatrixFilter[])[0].matrix = Colors.flashRed(
					Geom.remap(0, 400, this.takingDamageAnimationProgress, 0.6, 0)
				);
			} else {
				this.sprite.filters = []
				this.takingDamageAnimationProgress = -1;
			}
		}
	}

	public dealDamage(game: Game, amount: number) {
		this.aggro = true;
		this.takingDamageAnimationProgress = 0;
		const filter = new ColorMatrixFilter();
		this.sprite.filters = [filter];
		this.health -= amount;
		game.texts.push(new DamageTick(game.camera.container, this, amount))
	}

	public deathDuration = 500;

	private updateDeathAnimation(deltaMS: number) {
		this.sprite.alpha = this.deathDuration / 500;
		this.deathDuration -= deltaMS;
		if (this.deathDuration < 0) {
			this.destroy();
		}
	}

	// region ITransform

	public get x(): number {
		return this.container.x;
	}

	public set x(value: number) {
		this.container.x = value;
	}

	public get y(): number {
		return this.container.y;
	}

	public set y(value: number) {
		this.container.y = value;
	}

	public get w(): number {
		return this.container.width;
	}

	public get h(): number {
		return this.container.height;
	}

	// endregion
}
